// Migrated onto the house MenuPanel chassis (DESIGN_LANGUAGE.md §7.7,
// bespoke-menus wave): MenuPanel shell; the CanvasSearchItem pill rows are
// compound result rows (type icon + key + per-type action buttons), not
// menu-command rows, so they keep their own chassis — same call as the
// EnvItem/DomainItem cards in Dropdown.Envs. Deleted with the re-chassis: the
// hand-rolled `padding: 'Z1 A'` shell (MenuPanel pads Y) and the list's
// `margin: '- -Z2'` pull (it compensated that padding; under the Y body it
// would overflow by Z2 − Y and clip).
//
// Both consumers (SearchBar, ComponentAddressBar) merge this body INTO the
// brand Dropdown wrapper — `extends: ['Dropdown', 'CanvasSearchDropdown']`,
// menu body LAST so the MenuPanel surface (dialog theme, radiusCard, overlay
// elevation) wins over the wrapper's legacy common-box chrome while the
// wrapper keeps contributing positioning, flip logic and visibility. The
// wrapper's bolted-on @dark/@light boxShadow pair is nulled here: §7.7 puts
// menus on the `overlay` elevation token, and the pair's case-block
// !important would otherwise beat the token in both schemes.
export const CanvasSearchDropdown = {
  extends: 'MenuPanel',
  gap: 'A',
  minWidth: 'H',
  scope: {},
  '@dark': {
    boxShadow: null
  },
  '@light': {
    boxShadow: null
  },
  CaptionTitle: {
    // Align the caption with the house Z1 label inset.
    padding: '- Z1',
    Text: {
      text: (el, s) =>
        el.call(
          'polyglot',
          s.searchTerm ? 'editor.canvasSearch.results' : 'editor.canvasSearch.recent'
        )
    }
  },
  Items: {
    // Z gap between the radiusPill result rows — the dense result list keeps
    // its pill rhythm (rows own their inset), so no gap-0 command-list here.
    gap: 'Z',
    flow: 'y',
    onInit: async (el, s) => {
      const fuse = await import('fuse.js')

      // LIVENESS GATE — the page can change while that import is pending, and
      // an `onInit` that resumes into a removed element reads null. smbls
      // `dispose()` nulls BOTH `element.node` and `element.parent`
      // (packages/element/src/dispose.js), so the read of
      // `el.parent.typesToIndex` below threw
      // `Cannot read properties of null (reading 'typesToIndex')` on the
      // served catalog every time pagination replaced the grid mid-import.
      // An ordinary user reproduces it by navigating during the same window.
      //
      // The test is the DISPOSE SIGNATURE, deliberately not
      // `node.isConnected`. The framework learned that lesson first: the
      // onFrame loop in smbls packages/element/src/create.js used to return
      // early on `!node.isConnected` and killed itself, because during an
      // initial top-down mount a child completes before the ROOT is attached
      // to the document. Its comment states the rule this line follows — the
      // element is dead only when it was disposed. `__tests__/
      // asyncLifecycleLiveness.test.js` keeps a control on the mid-mount case
      // so nobody trades this back for an isConnected check.
      if (!el.node || !el.parent) return

      const Fuse = fuse.default

      const fuseOptions = {
        isCaseSensitive: false,
        shouldSort: true,
        findAllMatches: true,
        includeScore: true,
        threshold: 0.3,
        keys: ['key', 'title', 'code']
      }

      // Arrow form, not `function mapBucketToItems () {}` — FA207: frank
      // re-emits each handler as standalone source, and a nested function
      // DECLARATION inside it does not survive that rewrite.
      const mapBucketToItems = (bucketObj, type) => {
        if (!bucketObj) return []
        return Object.values(bucketObj)
          .map((item) => ({
            key: item.key,
            title: item.title || '',
            code: item.code || '',
            type
          }))
          .filter((v) => v.key)
      }

      const typesArr = el.parent.typesToIndex || [
        'components',
        'pages',
        'functions',
        'state',
        'files'
      ]
      const searchItems = typesArr.flatMap((bucket) =>
        mapBucketToItems(el.getSchema?.(bucket), bucket)
      )

      el.scope.fuse = new Fuse(searchItems, fuseOptions)
    },
    children: (el, s) => {
      if (!s.searchTerm) {
        return s.recents
      }

      const fuse = el.scope.fuse
      // The index is built by an ASYNC onInit, so a keystroke can land before
      // it exists — and now also on the removal path above, which leaves the
      // slot empty on purpose. Answer the recents rather than throw.
      if (!fuse) return s.recents || []

      // Arrow form — FA207, same reason as mapBucketToItems above.
      const searchAll = (query) =>
        fuse.search(query).map((r) => ({
          key: r.item.key,
          title: r.item.title,
          code: r.item.code,
          type: r.item.type, // component / page / function,
          score: r.score
        }))

      const result = searchAll(s.searchTerm)
      return result
    },
    childrenAs: 'state',
    childExtends: 'CanvasSearchItem'
  }
}
